<template>
  <SectionPageShell
    section="packages"
    :title="packageTypeLabel || 'Paket verwalten'"
    :subtitle="activePackage ? 'Paket verwalten' : null"
    show-back-link
  >
    <PackagePaymentMethods />

    <div
      class="package-manage py-3 py-lg-4"
      :class="manageThemeClass"
      :style="manageThemeStyle"
    >
    <p v-if="!isLoading && !loadError" class="text-muted mb-4">
      Hier können Sie Ihr aktuelles Paket hochstufen, widerrufen oder kündigen.
    </p>

    <div v-if="isLoading" class="alert alert-light border d-flex align-items-center gap-2">
      <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
      Paketdaten werden geladen...
    </div>

    <div v-else-if="loadError" class="alert alert-danger border">
      {{ loadError }}
    </div>

    <template v-else-if="activePackage">
      <article class="manage-card manage-card--active mb-4">
        <div class="manage-card__eyebrow">Aktives Paket</div>
        <h2 class="manage-card__title">{{ packageTypeLabel }}</h2>

        <div class="manage-card__meta">
          <div class="manage-card__line">
            <span>Kontingent</span>
            <strong>{{ quantityLabel }}</strong>
          </div>
          <div class="manage-card__line">
            <span>Status</span>
            <strong>{{ activePackage.ends_at ? `gekündigt (endet am ${formatDate(activePackage.ends_at)})` : 'läuft' }}</strong>
          </div>
          <div class="manage-card__line">
            <span>Verlängerung am</span>
            <strong>{{ nextBillingPeriodLabel }}</strong>
          </div>
          <div class="manage-card__line">
            <span>Basispreis</span>
            <strong>
              <PriceAmount :cents="packageBasePriceCents" monthly inline />
            </strong>
          </div>
        </div>

        <div class="d-grid gap-2 mt-4">
          <button
            v-if="activePackage.ends_at"
            type="button"
            class="btn btn-success btn-lg manage-btn-primary"
            :disabled="isReactivating"
            @click="continueRecurring"
          >
            {{ isReactivating ? 'Reaktivierung läuft...' : `endet am: ${formatDate(activePackage.ends_at)} - jetzt reaktivieren` }}
          </button>
        </div>
      </article>

      <article class="manage-card mb-4" v-if="higherTierPackages.length && !activePackage.ends_at">
        <div class="manage-card__eyebrow">Paket hochstufen</div>
        <h3 class="h5 mb-3">Verfügbare höhere Pakete</h3>
        <PackageContractTerms class="manage-contract-terms" />

        <div class="manage-upgrades">
          <div v-for="pkg in higherTierPackages" :key="`upgrade-${pkg.id}`" class="manage-upgrade-item">
            <div>
              <div class="manage-upgrade-item__title">{{ getTierLabel(pkg) }}</div>
              <div class="manage-upgrade-item__meta">
                <PriceAmount :cents="pkg.price_cents" monthly inline />
              </div>
            </div>
            <router-link
              :to="{name: 'packages.book', params: {id: assignmentId}, query: {...$route.query, upgrade_package_id: pkg.id}}"
              class="btn btn-success manage-btn-primary"
            >
              Paket hochstufen
            </router-link>
          </div>
        </div>
      </article>

      <article class="manage-card manage-card--warning mt-4" v-if="canWithdrawContract">
        <div class="manage-card__eyebrow">Vertrag widerrufen</div>
        <h3 class="h5 mb-2">Widerruf innerhalb der 14-tägigen Frist</h3>
        <p class="mb-3">
          Sie können diesen Vertrag noch widerrufen, da die Paketbuchung am
          <strong>{{ formatDate(packageAssignment.created_at) }}</strong> erstellt wurde.
        </p>
        <button
          type="button"
          class="btn btn-warning btn-lg w-100"
          :disabled="isWithdrawing"
          @click="withdrawContract"
        >
          {{ isWithdrawing ? 'Widerruf wird durchgeführt...' : 'Vertrag widerrufen' }}
        </button>
      </article>

      <article class="manage-card manage-card--danger mt-4" v-if="activePackage.source_kind !== 'free' && !activePackage.ends_at">
        <div class="manage-card__eyebrow">Paket kündigen</div>
        <h3 class="h5 mb-2">Kündigung zum Ende der laufenden Periode</h3>
        <p class="mb-3">
          Die Kündigung wird zum Ende der laufenden Periode wirksam: <strong>{{ nextBillingPeriodStartLabel }}</strong>.
          Danach sind die Paketleistungen nicht mehr verfügbar.
        </p>
        <button
          type="button"
          class="btn btn-danger btn-lg w-100"
          :disabled="isCancelling"
          @click="cancelRecurring"
        >
          {{ isCancelling ? 'Kündigung wird durchgeführt...' : 'Kündigung final bestätigen' }}
        </button>
      </article>
    </template>

    <div v-else class="alert alert-warning border">
      Das aktive Paket konnte nicht gefunden werden.
    </div>
    </div>
  </SectionPageShell>
</template>

<script>
import SectionPageShell from "@/components/base_type/SectionPageShell.vue";
import PriceAmount from "@/components/general/PriceAmount.vue";
import {usePackagesStore} from "@/stores/packages";
import {useUserStore} from "@/stores/user";
import PackageContractTerms from "@/components/packages/PackageContractTerms.vue";
import PackagePaymentMethods from "@/components/packages/PackagePaymentMethods.vue";
import {getBaseTypeThemeClass, getBaseTypeThemeStyle, resolveBaseTypeKey} from "@/utils/baseTypeTheme";
import Swal from "sweetalert2";

const PACKAGE_LABELS = {
  unterkunft: 'Unterkunft-Paket',
  entdecken: 'Entdecken-Paket',
  termin: 'Veranstaltungs-Paket',
  termine: 'Veranstaltungs-Paket',
  angebote: 'Angebots-Paket',
}

export default {
  name: 'PackagesManage',
  components: {
    PackageContractTerms,
    PackagePaymentMethods,
    PriceAmount,
    SectionPageShell,
  },
  setup() {
    return {
      packagesStore: usePackagesStore(),
      userStore: useUserStore(),
    }
  },
  data() {
    return {
      isLoading: true,
      loadError: '',
      isReactivating: false,
      isCancelling: false,
      isWithdrawing: false,
      packageAssignment: null,
    }
  },
  computed: {
    assignmentId() {
      return String(this.$route.params.id || '')
    },
    activePackage() {
      if (this.packageAssignment?.package) {
        return {
          ...this.packageAssignment.package,
          ends_at: this.packageAssignment.ends_at,
          created_at: this.packageAssignment.created_at,
          withdrawal_waiver_at: this.packageAssignment.withdrawal_waiver_at,
          extra_quantity: this.packageAssignment.extra_quantity,
        }
      }
      return null
    },
    packageTypeLabel() {
      return PACKAGE_LABELS[this.activePackage?.base_type] || 'Vermarktungs-Paket'
    },
    quantityLabel() {
      const quantity = Number(this.activePackage?.effective_quantity ?? this.activePackage?.quantity ?? 0)
      if (!quantity) {
        return 'Flexible Anzahl an Einträgen'
      }
      return `${quantity} ${quantity === 1 ? 'Eintrag' : 'Einträge'}`
    },
    higherTierPackages() {
      if (!this.activePackage) {
        return []
      }

      const currentPrice = Number(this.activePackage?.price_cents ?? 0)
      return this.packagesStore.items
        .filter((pkg) => (
          pkg?.base_type === this.activePackage.base_type
          && pkg?.source_kind === 'paid'
          && Number(pkg?.price_cents ?? 0) > currentPrice
          && Number(pkg?.id) !== Number(this.activePackage.id)
        ))
        .sort((a, b) => Number(a?.price_cents ?? 0) - Number(b?.price_cents ?? 0))
    },
    packageBasePriceCents() {
      return Number(this.activePackage?.price_cents ?? 0)
    },
    currentBillingPeriodBounds() {
      const createdAt = this.packageAssignment?.created_at
      if (!createdAt) {
        return null
      }

      const now = new Date()
      const assignmentCreatedAt = new Date(createdAt)
      if (Number.isNaN(assignmentCreatedAt.getTime())) {
        return null
      }

      let periodStart = new Date(assignmentCreatedAt)
      let hasNextPeriod = true
      while (hasNextPeriod) {
        const nextPeriodStart = new Date(periodStart)
        nextPeriodStart.setMonth(nextPeriodStart.getMonth() + 1)
        if (nextPeriodStart <= now) {
          periodStart = nextPeriodStart
          continue
        }
        hasNextPeriod = false
      }

      const periodEnd = new Date(periodStart)
      periodEnd.setMonth(periodEnd.getMonth() + 1)

      return {periodStart, periodEnd}
    },
    remainingRatio() {
      const bounds = this.currentBillingPeriodBounds
      if (!bounds) {
        return 1
      }

      const now = new Date()
      const periodStartDate = new Date(bounds.periodStart.getFullYear(), bounds.periodStart.getMonth(), bounds.periodStart.getDate())
      const periodEndDate = new Date(bounds.periodEnd.getFullYear(), bounds.periodEnd.getMonth(), bounds.periodEnd.getDate())
      const nowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate())

      const dayMs = 24 * 60 * 60 * 1000
      const totalDays = Math.floor((periodEndDate.getTime() - periodStartDate.getTime()) / dayMs)
      if (totalDays <= 0) {
        return 0
      }

      let elapsedDays = Math.max(Math.floor((nowDate.getTime() - periodStartDate.getTime()) / dayMs), 0)
      elapsedDays = Math.min(elapsedDays, totalDays)
      const remainingDays = totalDays - elapsedDays
      return remainingDays / totalDays
    },
    nextBillingPeriodStartLabel() {
      const bounds = this.currentBillingPeriodBounds
      if (!bounds) {
        return '-'
      }

      return bounds.periodEnd.toLocaleDateString('de-DE')
    },
    nextBillingPeriodLabel() {
      if (this.activePackage?.ends_at) {
        return 'gekündigt'
      }
      return this.nextBillingPeriodStartLabel
    },
    canWithdrawContract() {
      if (!this.packageAssignment || this.activePackage?.source_kind !== 'paid') {
        return false
      }
      if (this.packageAssignment.withdrawal_waiver_at) {
        return false
      }

      const createdAt = new Date(this.packageAssignment.created_at)
      if (Number.isNaN(createdAt.getTime())) {
        return false
      }

      const withdrawalPeriodMs = 14 * 24 * 60 * 60 * 1000
      return Date.now() - createdAt.getTime() <= withdrawalPeriodMs
    },
    manageBaseType() {
      const baseType = this.activePackage?.base_type
      if (!baseType) {
        return null
      }
      return resolveBaseTypeKey(baseType)
    },
    manageThemeClass() {
      return this.manageBaseType ? getBaseTypeThemeClass(this.manageBaseType) : ''
    },
    manageThemeStyle() {
      return this.manageBaseType ? getBaseTypeThemeStyle(this.manageBaseType) : {}
    },
  },
  async created() {
    await Promise.all([
      this.fetchPackageAssignment(),
      this.packagesStore.index(),
    ])
  },
  methods: {
    async fetchPackageAssignment() {
      this.isLoading = true
      this.loadError = ''

      try {
        const kdnr = this.userStore.user?.organisation?.kdnr || this.$route.query.kdnr
        const {data} = await this.packagesStore.apiClientV2.get(`/package_assignments/${this.assignmentId}`, {
          params: {
            ...(kdnr ? {kdnr} : {}),
          },
        })
        this.packageAssignment = data?.package_assignment || null
      } catch (error) {
        this.loadError = error?.response?.data?.message || 'Paketdaten konnten nicht geladen werden.'
      } finally {
        this.isLoading = false
      }
    },
    getTierLabel(pkg) {
      const quantity = Number(pkg?.quantity ?? pkg?.effective_quantity ?? 0)
      if (!quantity) {
        return 'Flexible Paketgröße'
      }
      return `${quantity} ${quantity === 1 ? 'Eintrag' : 'Einträge'}`
    },
    async cancelRecurring() {
      const confirmation = await Swal.fire({
        title: 'Paket wirklich kündigen?',
        html: `Die Kündigung wird zum Ende der laufenden Periode wirksam: <strong>${this.nextBillingPeriodStartLabel}</strong>.<br>Danach sind die Paketleistungen nicht mehr verfügbar.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Ja, kündigen',
        cancelButtonText: 'Abbrechen',
        confirmButtonColor: '#c95f5f',
        focusCancel: true,
      })

      if (!confirmation.isConfirmed) {
        return
      }

      this.isCancelling = true

      try {
        const kdnr = this.userStore.user?.organisation?.kdnr || this.$route.query.kdnr
        await this.packagesStore.apiClientV2.post(`/payments/${this.assignmentId}/cancel_recurring`, {
          ...(kdnr ? {kdnr} : {}),
        })

        await this.userStore.apiUserData()
        await this.packagesStore.index()
        await this.fetchPackageAssignment()
      } catch (error) {
        if (error?.response) {
          return
        }

        await Swal.fire(
          'Kündigung nicht möglich',
          error?.message || 'Bitte versuchen Sie es erneut.',
          'error'
        )
      } finally {
        this.isCancelling = false
      }
    },
    async withdrawContract() {
      const confirmation = await Swal.fire({
        title: 'Vertrag wirklich widerrufen?',
        html: 'Der Widerruf beendet den Vertrag innerhalb der 14-tägigen Widerrufsfrist.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Ja, widerrufen',
        cancelButtonText: 'Abbrechen',
        confirmButtonColor: '#c95f5f',
        focusCancel: true,
      })

      if (!confirmation.isConfirmed) {
        return
      }

      this.isWithdrawing = true

      try {
        const kdnr = this.userStore.user?.organisation?.kdnr || this.$route.query.kdnr
        await this.packagesStore.apiClientV2.post(`/payments/${this.assignmentId}/withdrawal`, {
          ...(kdnr ? {kdnr} : {}),
        })

        await this.userStore.apiUserData()
        await this.packagesStore.index()
        await Swal.fire('Widerruf durchgeführt', 'Der Vertrag wurde widerrufen.', 'success')
        await this.$router.push({name: 'packages.index', query: this.$route.query})
      } catch (error) {
        if (error?.response) {
          return
        }

        await Swal.fire(
          'Widerruf nicht möglich',
          error?.message || 'Bitte versuchen Sie es erneut.',
          'error'
        )
      } finally {
        this.isWithdrawing = false
      }
    },
    async continueRecurring() {
      this.isReactivating = true

      try {
        const kdnr = this.userStore.user?.organisation?.kdnr || this.$route.query.kdnr
        await this.packagesStore.apiClientV2.post(`/payments/${this.assignmentId}/continue_recurring`, {
          ...(kdnr ? {kdnr} : {}),
        })

        await this.userStore.apiUserData()
        await this.packagesStore.index()
        await this.fetchPackageAssignment()
      } catch (error) {
        if (error?.response) {
          return
        }

        await Swal.fire(
          'Reaktivierung nicht möglich',
          error?.message || 'Bitte versuchen Sie es erneut.',
          'error'
        )
      } finally {
        this.isReactivating = false
      }
    },
    formatDate(value) {
      if (!value) {
        return '-'
      }

      const date = new Date(value)
      if (Number.isNaN(date.getTime())) {
        return '-'
      }

      return date.toLocaleDateString('de-DE')
    },
  },
}
</script>

<style scoped lang="scss">
.manage-card {
  padding: 1.5rem;
  border: 1px solid #d8e1ea;
  background: #ffffff;
}

.manage-card--active {
  border-color: var(--base-type-border, #d8e1ea);
  border-top: 0.35rem solid var(--base-type-primary, #2f7ca8);
  background:
    radial-gradient(circle at top right, rgba(47, 124, 168, 0.12), transparent 38%),
    linear-gradient(180deg, #ffffff, var(--base-type-soft, #f7fbfd));
}

.manage-card__eyebrow {
  margin-bottom: 0.4rem;
  color: #5d7183;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.manage-card__title {
  margin-bottom: 0.9rem;
  color: #10273d;
}

.manage-card__meta {
  border-top: 1px solid #e7edf3;
  padding-top: 0.7rem;
}

.manage-card__line {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.35rem 0;
}

.manage-card__line span {
  color: #5d7183;
}

.manage-card__line strong {
  color: #10273d;
  text-align: right;
}

.manage-card__line--total {
  border-top: 1px solid #e7edf3;
  margin-top: 0.2rem;
  padding-top: 0.7rem;
}

.manage-upgrades {
  display: grid;
  gap: 0.7rem;
}

.manage-contract-terms {
  margin-bottom: 1rem;
}

.manage-upgrade-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 0.8rem;
  border: 1px solid var(--base-type-border, #e1e9f1);
  background: var(--base-type-surface, #f8fbff);
}

.manage-upgrade-item__title {
  color: #10273d;
  font-weight: 600;
}

.manage-upgrade-item__meta {
  color: #5d7183;
  font-size: 0.9rem;
}

.manage-slider-wrap {
  padding: 1rem;
  border: 1px solid #e6edf4;
  background: #f9fbfd;
}

.manage-slider-scale {
  display: flex;
  justify-content: space-between;
  margin-top: 0.3rem;
  color: #6a8092;
  font-size: 0.82rem;
}

.manage-card--danger {
  border-color: #f0c4c4;
  background: linear-gradient(180deg, #fff8f8, #fff2f2);
}

.manage-card--warning {
  border-color: #f1dc9b;
  background: linear-gradient(180deg, #fffdf6, #fff7df);
}

.manage-btn-primary {
  border: 0;
  background: linear-gradient(135deg, var(--base-type-primary, #1b6c97), var(--base-type-secondary, #30b9ec));
  box-shadow: 0 12px 24px rgba(16, 39, 61, 0.18);
}
</style>
